import { useState, useEffect, useRef } from 'react';
import {
    Box, Heading, VStack, Card, CardBody, Text,
    Button, Badge, Avatar, HStack, useToast, Icon, SimpleGrid,
    Alert, AlertIcon, Progress, Stat, StatLabel, StatNumber, StatGroup,
    Flex, Spinner, Input, Wrap, WrapItem,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay
} from '@chakra-ui/react';
import { FiMapPin, FiCheckCircle, FiClock, FiUser, FiActivity, FiNavigation, FiPlus, FiTrash2, FiEdit, FiSearch, FiPhone, FiTruck, FiWifiOff, FiX } from 'react-icons/fi';
import { IoCarSportOutline } from 'react-icons/io5';
import { authAPI } from '../services/api';
import LoadingOverlay from '../components/LoadingOverlay';
import LogoPulseLoader from '../components/LogoPulseLoader';
import PageWrapper from '../components/parent/PageWrapper';
import PulseLoading from '../components/PulseLoading';
// Removed: Local Geolocation imports (handled in Context)
import { AppLauncher } from '@capacitor/app-launcher';
import { useGPS } from '../context/GPSContext'; // New Import
import { isSimplePickupChild, isCarPlateChild } from '../utils/institutionFeatures';

// [A2] 簡化接送：孩子機構 features.simple_pickup=true 時（如幼稚園），不看狀態，
// 只要不是「家長已出發」或「已完成接送」就能發起接送——對應後端 /pickups/start 的放寬邏輯
// （app/routers/pickups.py）。simple_pickup=false（天籟等既有機構）時這裡的判斷式與
// A2 之前的固定清單逐字相同，行為零改變。
const isEligibleForPickup = (child) => {
    if (isSimplePickupChild(child)) {
        return !['PARENT_EN_ROUTE', 'PICKUP_COMPLETED'].includes(child.status);
    }
    return ['ARRIVED', 'HOMEWORK_PENDING', 'READY_FOR_PICKUP'].includes(child.status);
};

const ParentPickupPage = () => {
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [batchLoading, setBatchLoading] = useState(false);

    // [A3] 家長已登錄的常用車號（最多 2 組），供出發確認 dialog 快選
    const [myCarPlates, setMyCarPlates] = useState([]);
    // 出發確認 dialog 目前選中的車號（SINGLE 專用；BATCH 直接用 myCarPlates[0]，無 UI）
    const [carPlateChoice, setCarPlateChoice] = useState('');
    const [carPlateCustomMode, setCarPlateCustomMode] = useState(false);

    // GPS from Context
    const { gpsStatus, gpsInfo, startTracking, stopTracking, syncTrackingStudents } = useGPS();

    // Permission Dialog State (Can be kept if we want to prompt specifically for "Always allow" manually, 
    // but Context handles basic permissions. Keep manual guide for iOS if needed.)
    const [showPermissionAlert, setShowPermissionAlert] = useState(false);

    const toast = useToast();

    // Refs for Dialogs
    const cancelRef = useRef();
    const permissionCancelRef = useRef();

    const fetchData = async () => {
        try {
            const res = await authAPI.getMe();
            if (res.data.children) {
                setChildren(res.data.children);
                // Sync Context with backend status (Restores tracking if app was killed)
                syncTrackingStudents(res.data.children);
            }
            // [A3] 家長本人已登錄的車號（settings 頁維護），供出發時快選
            setMyCarPlates(res.data.car_plates || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 5000);

        const handleRefresh = () => {
            console.log("Push notification received, refreshing pickup status...");
            fetchData();
        };
        window.addEventListener('data-refresh', handleRefresh);

        return () => {
            clearInterval(interval);
            window.removeEventListener('data-refresh', handleRefresh);
        };
    }, []);

    const openAppSettings = async () => {
        try {
            await AppLauncher.openUrl({ url: 'app-settings:' });
            setShowPermissionAlert(false);
        } catch (e) {
            console.error("Failed to open settings:", e);
            toast({ title: "無法開啟設定頁面", status: "error" });
        }
    };

    const handleStartPickup = async (studentId, carPlate = null) => {
        // Optimistic Update
        setChildren(prev => prev.map(c => c.id === studentId ? { ...c, status: 'PARENT_EN_ROUTE' } : c));

        // Start GPS via Context
        startTracking([studentId]);

        try {
            setActionLoading(studentId);
            await authAPI.startPickup(studentId, carPlate);
            toast({
                title: "已發送接送通知",
                description: "GPS 定位已啟動，請小心駕駛。",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            setChildren(prev => prev.map(c => c.id === studentId ? { ...c, status: 'READY_FOR_PICKUP' } : c));
            fetchData();
            toast({ title: "發送失敗", status: "error" });
        } finally {
            setActionLoading(null);
        }
    };

    const handleCancelPickup = async (studentId) => {
        // 樂觀更新（先顯示可接送，伺服器會還原成真正的出發前狀態，fetchData 校正）
        setChildren(prev => prev.map(c => c.id === studentId ? { ...c, status: 'READY_FOR_PICKUP' } : c));
        // 停止 GPS 追蹤
        stopTracking(studentId);
        try {
            setActionLoading(studentId);
            await authAPI.cancelPickup(studentId);
            toast({
                title: "已取消接送",
                description: "GPS 追蹤已停止，老師已收到通知。",
                status: "info",
                duration: 4000,
                isClosable: true,
            });
            fetchData(); // 取回伺服器還原後的狀態
        } catch (error) {
            // 取消失敗 → 還原顯示 + 重新追蹤
            setChildren(prev => prev.map(c => c.id === studentId ? { ...c, status: 'PARENT_EN_ROUTE' } : c));
            startTracking([studentId]);
            fetchData();
            toast({ title: "取消失敗，請稍後再試", status: "error" });
        } finally {
            setActionLoading(null);
        }
    };

    const StatusBadge = ({ status }) => {
        const config = {
            'NOT_ARRIVED': { color: 'gray', label: '未到班' },
            'ARRIVED': { color: 'blue', label: '已進班' },
            'HOMEWORK_PENDING': { color: 'orange', label: '留班加強中' },
            'READY_FOR_PICKUP': { color: 'green', label: '可接送' },
            'PARENT_EN_ROUTE': { color: 'red', label: '家長路途中' },
            'PICKUP_COMPLETED': { color: 'gray', label: '已回家' },
            'LEAVE': { color: 'yellow', label: '請假' },
        }[status] || { color: 'gray', label: status };

        return <Badge colorScheme={config.color} fontSize="md" px={3} py={1} borderRadius="md">{config.label}</Badge>;
    };

    const eligibleForPickup = children.filter(isEligibleForPickup);

    const handleBatchPickup = async () => {
        if (eligibleForPickup.length === 0) return;
        const originalChildren = [...children];
        const targetIds = eligibleForPickup.map(c => c.id);

        setChildren(prev => prev.map(c => targetIds.includes(c.id) ? { ...c, status: 'PARENT_EN_ROUTE' } : c));

        // Context Call - Now Works!
        startTracking(targetIds);

        // [A3] 批量接送不提供車號選擇 UI，直接用已登錄的第一組（沒有登錄車號的機構/家長
        // 則不帶 car_plate，行為與 A3 之前逐字相同）。
        const defaultCarPlate = myCarPlates[0] || null;

        try {
            await Promise.all(eligibleForPickup.map(child =>
                authAPI.startPickup(child.id, isCarPlateChild(child) ? defaultCarPlate : null)
            ));
            toast({ title: "已發送接送通知", status: "success", duration: 3000 });
        } catch (error) {
            setChildren(originalChildren);
            toast({ title: "接送請求失敗", status: "error" });
            fetchData();
        }
    };

    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        type: null,
        targetId: null,
        targetName: ''
    });

    const onOpenConfirm = (type, targetId = null, targetName = '') => {
        // [A3] SINGLE 且該孩子機構開啟車號功能時，預帶已登錄的第一組（可在 dialog 內切換/自行輸入）
        if (type === 'SINGLE') {
            const child = children.find(c => c.id === targetId);
            if (isCarPlateChild(child)) {
                setCarPlateChoice(myCarPlates[0] || '');
                setCarPlateCustomMode(myCarPlates.length === 0);
            } else {
                setCarPlateChoice('');
                setCarPlateCustomMode(false);
            }
        }
        setConfirmDialog({ isOpen: true, type, targetId, targetName });
    };

    const onCloseConfirm = () => {
        setConfirmDialog({ isOpen: false, type: null, targetId: null, targetName: '' });
    };

    const onConfirmAction = () => {
        const { type, targetId } = confirmDialog;
        onCloseConfirm();
        if (type === 'SINGLE') {
            const child = children.find(c => c.id === targetId);
            handleStartPickup(targetId, isCarPlateChild(child) ? (carPlateChoice || null) : null);
        } else if (type === 'BATCH') {
            handleBatchPickup();
        } else if (type === 'CANCEL') {
            handleCancelPickup(targetId);
        }
    };

    return (
        <Box pb="120px" minH="100vh">
            {/* Permission Guide Dialog */}
            <AlertDialog
                isOpen={showPermissionAlert}
                leastDestructiveRef={permissionCancelRef}
                onClose={() => setShowPermissionAlert(false)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius="xl" mx={4}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            ⚠️ 重要設定提示
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {Capacitor.getPlatform() === 'ios' ? (
                                <>
                                    為確保您在接送途中（鎖屏或背景執行）能持續傳送位置給老師，iOS 系統要求您將「天籟徐薇」的定位權限設為「永遠/始終」。
                                </>
                            ) : (
                                <>
                                    為了防止 GPS 在背景（鎖屏）時中斷，建議您檢查以下兩點：
                                    <br />1. 定位權限設為「永遠允許」
                                    <br />2. 允許 App 在背景執行 (不被省電模式殺掉)
                                </>
                            )}
                            <br /><br />
                            請點擊下方按鈕，前往設定頁面進行確認。
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={permissionCancelRef} onClick={() => setShowPermissionAlert(false)} borderRadius="lg">
                                稍後再說
                            </Button>
                            <Button colorScheme="blue" onClick={openAppSettings} ml={3} borderRadius="lg">
                                前往設定
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Confirmation Alert Dialog */}
            <AlertDialog
                isOpen={confirmDialog.isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onCloseConfirm}
                isCentered
                motionPreset="slideInBottom"
            >
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius="xl" mx={4}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {confirmDialog.type === 'CANCEL' ? '確認取消接送？' : '確認開始接送？'}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {confirmDialog.type === 'CANCEL' ? (
                                <>
                                    您要取消接送 {confirmDialog.targetName} 嗎？
                                    <Text mt={2} color="onCanvas.400" fontSize="sm">
                                        取消後 GPS 追蹤會停止，老師會收到通知，學生狀態會回到接送前。
                                    </Text>
                                </>
                            ) : (
                                <>
                                    {confirmDialog.type === 'SINGLE'
                                        ? `您即將前往接送 ${confirmDialog.targetName}。`
                                        : `您即將前往接送 ${eligibleForPickup.map(c => c.full_name).join('、')}。`
                                    }
                                    <Text mt={2} color="onCanvas.400" fontSize="sm">
                                        點擊確認後，老師將會收到通知並協助學生做準備。
                                    </Text>
                                </>
                            )}

                            {/* [A3] 車號選擇：只在單一孩子出發、且該孩子機構開啟 car_plate 功能時顯示。
                                feature off 時（天籟等既有機構）這裡完全不渲染，dialog 與 A3 之前逐字相同。 */}
                            {confirmDialog.type === 'SINGLE' && isCarPlateChild(children.find(c => c.id === confirmDialog.targetId)) && (
                                <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.100">
                                    <Text fontWeight="bold" mb={2} display="flex" alignItems="center">
                                        <Icon as={IoCarSportOutline} mr={2} />
                                        本次車號
                                    </Text>
                                    {myCarPlates.length > 0 && (
                                        <Wrap mb={2}>
                                            {myCarPlates.map((plate) => (
                                                <WrapItem key={plate}>
                                                    <Button
                                                        size="sm"
                                                        variant={!carPlateCustomMode && carPlateChoice === plate ? 'solid' : 'outline'}
                                                        colorScheme="red"
                                                        borderRadius="md"
                                                        onClick={() => { setCarPlateChoice(plate); setCarPlateCustomMode(false); }}
                                                    >
                                                        {plate}
                                                    </Button>
                                                </WrapItem>
                                            ))}
                                            <WrapItem>
                                                <Button
                                                    size="sm"
                                                    variant={carPlateCustomMode ? 'solid' : 'outline'}
                                                    colorScheme="gray"
                                                    borderRadius="md"
                                                    onClick={() => { setCarPlateCustomMode(true); setCarPlateChoice(''); }}
                                                >
                                                    自行輸入
                                                </Button>
                                            </WrapItem>
                                        </Wrap>
                                    )}
                                    {(carPlateCustomMode || myCarPlates.length === 0) && (
                                        <Input
                                            placeholder="輸入本次車號"
                                            value={carPlateChoice}
                                            onChange={(e) => setCarPlateChoice(e.target.value)}
                                            size="sm"
                                            maxW="220px"
                                        />
                                    )}
                                </Box>
                            )}
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onCloseConfirm} borderRadius="lg">
                                {confirmDialog.type === 'CANCEL' ? '先不要' : '取消'}
                            </Button>
                            <Button colorScheme="red" onClick={onConfirmAction} ml={3} borderRadius="lg">
                                {confirmDialog.type === 'CANCEL' ? '確定取消接送' : '確定出發'}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <VStack spacing={6} align="stretch" py={6} px={4} maxW="600px" mx="auto">
                {(actionLoading || batchLoading) && <PulseLoading />}

                {loading && (
                    <Flex justify="center" align="center" py={8}>
                        <LogoPulseLoader />
                    </Flex>
                )}

                <VStack spacing={5} align="stretch">
                    {!loading && children.length === 0 && (
                        <Alert status="info" borderRadius="xl">
                            <AlertIcon />
                            目前尚無綁定的子女資料。
                        </Alert>
                    )}

                    {children.map(child => {
                        const gps = gpsInfo[child.id];
                        return (
                            <Card key={child.id} overflow="hidden" variant="outline"
                                borderColor={child.status === 'PARENT_EN_ROUTE' ? "red.300" : "gray.200"}
                                boxShadow="md"
                                borderWidth={child.status === 'PARENT_EN_ROUTE' ? "2px" : "1px"}
                                borderRadius="2xl"
                            >
                                <CardBody p={5}>
                                    <HStack spacing={4} mb={5}>
                                        <Avatar size="lg" name={child.full_name} bg="brand.500" color="white" />
                                        <Box>
                                            <Heading size="lg">{child.full_name}</Heading>
                                            <Text fontSize="md" color="onCanvas.400">{child.class?.name || '未分班'}</Text>
                                        </Box>
                                        {/* [A2] 簡化接送（如幼稚園）：App 端不顯示學生狀態徽章 */}
                                        {!isSimplePickupChild(child) && (
                                            <Box ml="auto">
                                                <StatusBadge status={child.status} />
                                            </Box>
                                        )}
                                    </HStack>

                                    <Box mt={4}>
                                        {isEligibleForPickup(child) && (
                                            <Button
                                                w="100%"
                                                size="lg"
                                                colorScheme="red"
                                                onClick={() => onOpenConfirm('SINGLE', child.id, child.full_name)}
                                                leftIcon={<FiActivity />}
                                                h="60px"
                                                fontSize="xl"
                                                borderRadius="xl"
                                                _active={{ transform: "scale(0.98)" }}
                                            >
                                                我要出發 (開始接送)
                                            </Button>
                                        )}

                                        {child.status === 'PARENT_EN_ROUTE' && (
                                            <Box bg="red.50" p={5} borderRadius="xl" textAlign="center">
                                                <HStack justify="center" mb={3} color="red.600">
                                                    <Icon as={FiNavigation} className="blink" boxSize={6} />
                                                    <Heading size="md">GPS 即時追蹤中</Heading>
                                                </HStack>

                                                {gps ? (
                                                    <StatGroup bg="white" p={4} borderRadius="xl" shadow="sm">
                                                        <Stat>
                                                            <StatLabel fontSize="md">預計抵達</StatLabel>
                                                            <StatNumber color="red.500" fontSize="3xl">{(gps.eta !== null && gps.eta !== undefined) ? gps.eta : '--'} <Text as="span" fontSize="lg">分</Text></StatNumber>
                                                        </Stat>
                                                        <Stat>
                                                            <StatLabel fontSize="md">剩餘距離</StatLabel>
                                                            <StatNumber color="blue.500" fontSize="3xl">{(gps.distance / 1000).toFixed(1)} <Text as="span" fontSize="lg">km</Text></StatNumber>
                                                        </Stat>
                                                    </StatGroup>
                                                ) : (
                                                    <Flex justify="center" direction="column" align="center" py={4} bg="white" borderRadius="xl">
                                                        {gpsStatus === 'ERROR' ? (
                                                            <>
                                                                <Icon as={FiWifiOff} boxSize={8} color="red.500" mb={2} />
                                                                <Text color="red.500">定位失敗，請檢查權限或訊號</Text>
                                                                {/* Context handles restart, but we can expose manual retry if we export it from context, or just let watchdog handle it */}
                                                                {/* <Button size="sm" mt={2} onClick={() => startGPS()} variant="outline" colorScheme="red">手動重試</Button> */}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Spinner size="lg" color="blue.500" mb={2} speed="0.8s" />
                                                                <Text fontSize="lg" color="gray.600" fontWeight="bold">
                                                                    {gpsStatus === 'RETRYING' ? '訊號微弱，重新定位中...' : '正在獲取路況...'}
                                                                </Text>
                                                                <Text fontSize="sm" color="gray.400" mt={1}>請保持 App 開啟以維持追蹤</Text>
                                                            </>
                                                        )}
                                                    </Flex>
                                                )}

                                                <Button
                                                    w="100%"
                                                    mt={4}
                                                    size="lg"
                                                    variant="outline"
                                                    colorScheme="red"
                                                    bg="white"
                                                    leftIcon={<FiX />}
                                                    onClick={() => onOpenConfirm('CANCEL', child.id, child.full_name)}
                                                    isLoading={actionLoading === child.id}
                                                    borderRadius="xl"
                                                >
                                                    取消接送
                                                </Button>
                                            </Box>
                                        )}

                                        {/* [A2] 簡化接送（如幼稚園）：不檢查到班狀態，不顯示「尚未到班」警告 */}
                                        {child.status === 'NOT_ARRIVED' && !isSimplePickupChild(child) && (
                                            <Alert status="warning" borderRadius="xl">
                                                <AlertIcon />
                                                <Text fontSize="md">學生尚未到班，無法發起接送。</Text>
                                            </Alert>
                                        )}

                                        {child.status === 'PICKUP_COMPLETED' && (
                                            <Alert status="success" borderRadius="xl">
                                                <AlertIcon />
                                                <Text fontSize="md">今日接送已完成。</Text>
                                            </Alert>
                                        )}
                                    </Box>
                                </CardBody>
                            </Card>
                        );
                    })}

                    {eligibleForPickup.length >= 2 && (
                        <Button
                            w="100%"
                            size="lg"
                            colorScheme="orange"
                            onClick={() => onOpenConfirm('BATCH')}
                            leftIcon={<FiTruck />}
                            mt={4}
                            boxShadow="lg"
                            h="70px"
                            fontSize="2xl"
                            borderRadius="2xl"
                            _active={{ transform: "scale(0.98)" }}
                        >
                            一次接走所有小孩
                        </Button>
                    )}
                </VStack>
            </VStack>
        </Box>
    );
};

export default ParentPickupPage;
